import React from "react";
import Sidebar from "../../components/Sidebar";
import Spinner from "../../components/Spinner";
import {
  fetchCachedRepos,
  fetchUserProviders,
  fetchRecentPipelines,
  fetchRecentTests,
  fetchPendingPRs
} from "../../api/client";
import "./MainPage.css";

export default function DashboardMainPage() {
  const user = React.useMemo(() => {
    const storedUser = localStorage.getItem('user');
    return storedUser ? JSON.parse(storedUser) : { name: "User" };
  }, []);

  type Repo = {
    id: string | number;
    url: string;
    display_name?: string;
    name: string;
    language?: string;
  };

  type Pipeline = {
    id: string | number;
    repo: string;
    status: string;
    branch: string;
    time: string;
  };

  type Test = {
    id: string | number;
    repo: string;
    status: string;
    branch: string;
    coverage?: string;
    time: string;
  };

  type PR = {
    id: string | number;
    url: string;
    repo: string;
    type: string;
    created: string;
  };

  const [repos, setRepos] = React.useState<Repo[]>([]);
  const [reposLoading, setReposLoading] = React.useState(true);

  const [pipelines, setPipelines] = React.useState<Pipeline[]>([]);
  const [pipelinesLoading, setPipelinesLoading] = React.useState(true);

  const [tests, setTests] = React.useState<Test[]>([]);
  const [testsLoading, setTestsLoading] = React.useState(true);

  const [prs, setPRs] = React.useState<PR[]>([]);
  const [prsLoading, setPRsLoading] = React.useState(true);

  const [providerConnected, setProviderConnected] = React.useState<boolean | null>(null);

  React.useEffect(() => {
    setProviderConnected(null);
    fetchUserProviders()
      .then(resp => setProviderConnected(resp.connected.length > 0))
      .catch(() => setProviderConnected(false));
  }, []);

  // Only fetch locked/cached repos (no spinner needed)
  React.useEffect(() => {
    if (providerConnected !== true) return;
    setReposLoading(true);
    fetchCachedRepos(true)
      .then(cachedRepos => setRepos(cachedRepos || []))
      .catch(() => setRepos([]))
      .finally(() => setReposLoading(false));

    setPipelinesLoading(true);
    setTestsLoading(true);
    setPRsLoading(true);

    fetchRecentPipelines()
      .then(setPipelines)
      .catch(console.error)
      .finally(() => setPipelinesLoading(false));
    fetchRecentTests()
      .then(setTests)
      .catch(console.error)
      .finally(() => setTestsLoading(false));
    fetchPendingPRs()
      .then(setPRs)
      .catch(console.error)
      .finally(() => setPRsLoading(false));
  }, [providerConnected]);

  return (
    <div className="dashboard-root">
      <Sidebar user={user} current="dashboard" />
      <div className="main-content">
        {providerConnected === null ? (
          <div className="repo-loading">
            <Spinner centered label="Checking code host connection..." />
          </div>
        ) : providerConnected === false ? (
          <div className="provider-disconnected">
            <div className="provider-disconnected-inner">
              <b>No code host connected.</b>
              <div style={{ height: 10 }} />
              <span>
                Go to{" "}
                <a href="/dashboard/settings" className="provider-link">
                  Account Settings
                </a>{" "}
                to connect GitHub or Gitea.
              </span>
            </div>
          </div>
        ) : (
          <div className="overview-grid">
            {/* Repos Card */}
            <div className="card">
              <div className="card-title">Enabled Repositories</div>
              {reposLoading ? (
                // No spinner needed, but you can show a short "Loading..." for a flash if you want:
                <div style={{ padding: "32px 0", textAlign: "center" }}>Loading...</div>
              ) : repos.length === 0 ? (
                <div className="empty">No enabled repositories</div>
              ) : (
                <ul className="enabled-list">
                  {repos.slice(0, 5).map(r =>
                    <li key={r.id}>
                      <a href={r.url} target="_blank" rel="noopener">{r.display_name || r.name}</a>
                      <span className="lang">{r.language}</span>
                    </li>
                  )}
                </ul>
              )}
            </div>
            {/* Pipelines Card */}
            <div className="card">
              <div className="card-title">Recent Pipelines</div>
              {pipelinesLoading ? (
                <Spinner centered />
              ) : (
                <table className="activity-table">
                  <thead>
                    <tr><th>Repo</th><th>Status</th><th>Branch</th><th>When</th></tr>
                  </thead>
                  <tbody>
                    {pipelines.length === 0 ? (
                      <tr><td colSpan={4} className="empty">No pipelines found</td></tr>
                    ) : pipelines.map(p => (
                      <tr key={p.id}>
                        <td>{p.repo}</td>
                        <td>
                          {p.status === "Success" ? <span className="status success">●</span> :
                            p.status === "Failed" ? <span className="status failed">●</span> :
                            <span className="status pending">●</span>}
                          {p.status}
                        </td>
                        <td>{p.branch}</td>
                        <td>{p.time}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            {/* Tests Card */}
            <div className="card">
              <div className="card-title">Recent Tests</div>
              {testsLoading ? (
                <Spinner centered />
              ) : (
                <table className="activity-table">
                  <thead>
                    <tr><th>Repo</th><th>Status</th><th>Branch</th><th>Coverage</th><th>When</th></tr>
                  </thead>
                  <tbody>
                    {tests.length === 0 ? (
                      <tr><td colSpan={5} className="empty">No test runs found</td></tr>
                    ) : tests.map(t => (
                      <tr key={t.id}>
                        <td>{t.repo}</td>
                        <td>
                          {t.status === "Success"
                            ? <span className="status success">●</span>
                            : t.status === "Failed"
                              ? <span className="status failed">●</span>
                              : <span className="status pending">●</span>
                          }
                          {t.status}
                        </td>
                        <td>{t.branch}</td>
                        <td>{t.coverage || "—"}</td>
                        <td>{t.time}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            {/* PRs Card */}
            <div className="card">
              <div className="card-title">Pending Pull Requests</div>
              {prsLoading ? (
                <Spinner centered />
              ) : (
                prs.length === 0
                  ? <div className="empty">No PRs pending</div>
                  : <ul className="pending-list">
                      {prs.map(pr => (
                        <li key={pr.id}>
                          <a href={pr.url}>{pr.repo}</a> <span>{pr.type}</span> <span>{pr.created}</span>
                        </li>
                      ))}
                    </ul>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
